// 🌐 AUTH-AWARE HTTP CLIENT
// ✅ Integrates with Redux store for authentication
// ✅ Automatic retry logic with exponential backoff
// ✅ Timeout handling with AbortController
// ✅ Automatic logout on 401 errors

import { store } from '../store/singleton.js';
import { logoutUser } from '../store/slices/auth.js';

export interface HttpClientConfig {
  baseUrl?: string;
  timeout?: number;
  retries?: number;
}

/** Extended options for individual requests — allows overriding timeout/retries per-call. */
export interface RequestOptions extends RequestInit {
  /** Override the default timeout (ms) for this request. */
  timeout?: number;
  /** Override the default retry count for this request. */
  retries?: number;
}

// Dev-only debug logging. In production these per-request 🐛/🔍/🚀/✅ logs were
// firing on every request (card l_Q1_9Vr_d8r) — gate them behind Vite's DEV flag
// so prod stays quiet. Optional-chained: import.meta.env is undefined in some
// non-Vite runtimes (unit tests / SSR entry).
const DEBUG_HTTP = import.meta.env?.DEV === true;

class HttpClient {
  private baseUrl: string;
  private timeout: number;
  private retries: number;

  constructor(config: HttpClientConfig = {}) {
    // Optional-chain import.meta.env: under Vite it's always defined, but in
    // non-Vite runtimes (unit tests / SSR entry) it can be undefined — guard so
    // constructing a client there falls back to the default base URL instead of
    // throwing on `undefined.VITE_API_URL`.
    this.baseUrl = config.baseUrl || import.meta.env?.VITE_API_URL || '/api/v1';
    this.timeout = config.timeout || 10000;
    this.retries = config.retries || 2;
  }

  async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    if (DEBUG_HTTP) {
      console.log('🔍 HTTP Client Request Details:', {
        baseUrl: this.baseUrl,
        endpoint,
        constructedUrl: url,
        windowOrigin: typeof window !== 'undefined' ? window.location.origin : 'N/A'
      });
    }

    // Per-call overrides (destructure before spreading into requestOptions)
    const requestTimeout = options.timeout ?? this.timeout;
    const requestRetries = options.retries ?? this.retries;
    // Remove our custom keys so they don't leak into the native RequestInit
    const { timeout: _t, retries: _r, ...nativeOptions } = options;

    // Auth is handled via HttpOnly session_id cookie (set by backend on login).
    // credentials: 'include' ensures the cookie is sent with every request.
    // No Bearer token needed for web — cookies are automatic.

    const requestOptions: RequestInit = {
      ...nativeOptions,
      credentials: 'include', // Send session_id cookie with every request
      headers: {
        // Only set Content-Type for requests with a body
        ...(options.body && { 'Content-Type': 'application/json' }),
        // Prevent HTTP-level caching (CDN, browser, proxy) for all requests
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        ...options.headers
      }
    };

    let lastError: Error;
    
    // Retry logic
    for (let attempt = 0; attempt <= requestRetries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), requestTimeout);

        if (DEBUG_HTTP) {console.log('🚀 HTTP Client BEFORE fetch:', { url, method: requestOptions.method, attempt });}

        const response = await fetch(url, {
          ...requestOptions,
          signal: controller.signal
        });

        if (DEBUG_HTTP) {console.log('✅ HTTP Client AFTER fetch:', { url, status: response.status, ok: response.ok });}

        clearTimeout(timeoutId);
        
        // On 401, don't auto-logout — let the caller handle it.
        // The auth system (initializeAuth / ProtectedRoute) handles session expiry.
        // Auto-logout here causes race conditions during login/init.

        if (!response.ok) {
          // Try to extract error message from response body
          let errorMessage = response.statusText;
          try {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const errorData = await response.json();
              if (errorData.error) {
                errorMessage = errorData.error;
              } else if (errorData.message) {
                errorMessage = errorData.message;
              }
            }
          } catch {
            // If we can't parse the response, use the status text
          }
          throw new Error(`HTTP ${response.status}: ${errorMessage}`);
        }
        
        // Handle responses with no content (like DELETE operations)
        if (response.status === 204 || response.headers.get('content-length') === '0') {
          return undefined as T;
        }
        
        // Check if response has content to parse
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          return data;
        }
        
        // For non-JSON responses, return undefined
        return undefined as T;
        
      } catch (error) {
        if (DEBUG_HTTP) {
          console.error('🚨 HTTP Client Error:', {
            attempt,
            url,
            error,
            errorMessage: error instanceof Error ? error.message : 'Unknown error',
            errorStack: error instanceof Error ? error.stack : undefined,
            errorType: error?.constructor?.name
          });
        }

        lastError = error instanceof Error ? error : new Error('Request failed');

        // Don't retry on auth errors or client errors (4xx)
        if (error instanceof Error) {
          if (error.message.includes('401') ||
              error.message.includes('Authentication') ||
              error.message.includes('HTTP 4')) {
            throw error;
          }
        }

        // Wait before retry (exponential backoff)
        if (attempt < requestRetries) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }
    }
    
    throw lastError!;
  }

  // Convenience methods
  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', ...options });
  }

  async post<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    if (DEBUG_HTTP) {
      console.log('🐛 DEBUG: HTTP Client - POST request', { endpoint, data, options });
    }

    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      ...options
    });
  }

  async put<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    if (DEBUG_HTTP) {
      console.log('🐛 DEBUG: HTTP Client - PUT request', { endpoint, data, options });
    }

    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      ...options
    });
  }

  async patch<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      ...options
    });
  }

  async delete<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: data ? JSON.stringify(data) : undefined,
      ...options
    });
  }
}

export const httpClient = new HttpClient();